import { useEffect, useState } from 'react';
import './VoicePickerSection.css';
import { useNarratorVoice, NARRATOR_VOICE_DEFAULT } from '../../hooks/useNarratorVoice';
import { getAuthHeaders } from '../../services/host/authHeaders';

/**
 * V1 Voice GM (2026-06-02) — narrator voice picker.
 *
 * Fetches the Kokoro voice catalog from /api/voice/voices?engine=kokoro
 * (vibe-designer routes/voice.py:1037-1126) and renders a <select> that
 * persists the choice via useNarratorVoice (localStorage). The selected
 * voice is consumed by ReadAloudControl at TTS-fetch time.
 *
 * Catalog shape (per route enrichment):
 *   {voices: [{id: "bm_lewis", gender: "male", description: "British English"}, ...]}
 *
 * Fetch failure path: keep the static fallback list (default + recommended
 * British voices) so the picker is usable even when the route is down.
 * The user's persisted choice survives across fetch failures.
 */

interface VoiceCatalogEntry {
  id: string;
  gender?: string;
  description?: string;
}

const FALLBACK_VOICES: VoiceCatalogEntry[] = [
  { id: 'bm_lewis', gender: 'male', description: 'British English' },
  { id: 'bf_emma', gender: 'female', description: 'British English' },
  { id: 'am_michael', gender: 'male', description: 'American English' },
  { id: 'af_heart', gender: 'female', description: 'American English' },
];

function formatVoiceLabel(v: VoiceCatalogEntry): string {
  const description = v.description ?? '';
  const gender = v.gender ?? '';
  const tag = [description, gender].filter((s) => s.length > 0).join(' ');
  return tag.length > 0 ? `${v.id} — ${tag}` : v.id;
}

export interface VoicePickerSectionProps {
  testidPrefix?: string;
}

export function VoicePickerSection({
  testidPrefix = 'br-voice-picker',
}: VoicePickerSectionProps) {
  const [voice, setVoice] = useNarratorVoice();
  const [catalog, setCatalog] = useState<VoiceCatalogEntry[]>(FALLBACK_VOICES);
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'error'>(
    'idle',
  );

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    fetch('/api/voice/voices?engine=kokoro', { headers: getAuthHeaders() })
      .then((r) => {
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }
        return r.json();
      })
      .then((data: { voices?: unknown }) => {
        if (cancelled) {
          return;
        }
        if (Array.isArray(data.voices)) {
          const normalized: VoiceCatalogEntry[] = [];
          for (const entry of data.voices) {
            if (entry && typeof entry === 'object' && 'id' in entry && typeof (entry as Record<string, unknown>).id === 'string') {
              const e = entry as Record<string, unknown>;
              normalized.push({
                id: e.id as string,
                gender: typeof e.gender === 'string' ? e.gender : undefined,
                description:
                  typeof e.description === 'string' ? e.description : undefined,
              });
            }
          }
          if (normalized.length > 0) {
            setCatalog(normalized);
          }
        }
        setLoadState('idle');
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // If the persisted voice is NOT in the catalog (e.g. user previously
  // picked a voice that's no longer registered), still render it as the
  // selected option so the <select> stays in sync with the hook.
  const catalogHasSelected = catalog.some((c) => c.id === voice);
  const renderList: VoiceCatalogEntry[] = catalogHasSelected
    ? catalog
    : [...catalog, { id: voice }];

  return (
    <section
      className="br-scenario-drawer-section br-voice-picker"
      data-testid={testidPrefix}
    >
      <h3 className="br-label">Narrator voice</h3>
      <label className="br-label" htmlFor={`${testidPrefix}-select`}>
        Voice
        <select
          id={`${testidPrefix}-select`}
          className="br-voice-picker-select"
          data-testid={`${testidPrefix}-select`}
          value={voice}
          onChange={(e) => setVoice(e.target.value)}
        >
          {renderList.map((v) => (
            <option key={v.id} value={v.id}>
              {formatVoiceLabel(v)}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className="br-voice-picker-reset"
        data-testid={`${testidPrefix}-reset`}
        onClick={() => setVoice(NARRATOR_VOICE_DEFAULT)}
        disabled={voice === NARRATOR_VOICE_DEFAULT}
      >
        Reset to default ({NARRATOR_VOICE_DEFAULT})
      </button>
      {loadState === 'error' && (
        <p
          className="br-label br-voice-picker-error"
          data-testid={`${testidPrefix}-error`}
        >
          Voice catalog unreachable — showing fallback list.
        </p>
      )}
    </section>
  );
}

export default VoicePickerSection;
